import { DateTime } from 'luxon';
import numbro from 'numbro';
import { capitalize } from 'vue';

import { fromServerDate } from '@/globals';
import formatNumber from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';
import Format = numbro.Format;
import { IGroupingByDimension } from './IGrouping';

export const REMAINDER_ID = 'REMAINDER';

interface ISummarizedReportGroupItemOptions extends IGroupingByDimension {
  id: string | number;
  name: string;
  impressions: number;
  cpm: number;
  revenue: number;
  advertiser_id?: number;
  creative_ids?: number[];
  start_date?: string;
  end_date?: string;
  dsp_ids?: number[];
  buyer_ids?: number[];
  deal_id?: number;
  logo?: string;
  from?: string;
  fromLogo?: string;
  sspId?: number;
  sspLogoUri?: string;
  sspName?: string;
  advertiser?: string;
}

export default class SummarizedReportGroupItem extends Validatable<number, string | number> {
  name: string;
  impressions: number;
  cpm: number;
  revenue: number;
  previous: SummarizedReportGroupItem;
  previousRanking: number;
  rankingChange: number;
  percentageOfTotal: number;

  advertiserName: string;
  advertiserID: number;
  creativeIDs: number[];
  startDate: DateTime;
  endDate: DateTime;

  dspIDs: number[];
  buyerIDs: number[];
  dealID: number;

  logo: string;
  from: string;
  fromLogo: string;

  sspId: number;
  sspLogoUri: string;
  sspName: string;

  constructor(options: ISummarizedReportGroupItemOptions) {
    super();
    this.id = options.id;
    this.name = options.name === REMAINDER_ID ? capitalize(i18n.global.t('names.other', { n: 2 })) : options.name;
    this.impressions = options.impressions;
    this.cpm = options.cpm;
    this.revenue = options.revenue;
    this.previous = null; // SummarizedReportGroupItem
    this.previousRanking = null;
    this.rankingChange = null;
    this.percentageOfTotal = null;

    // CAMPAIGN only
    this.advertiserID = options.advertiser_id;
    this.creativeIDs = options.creative_ids;
    this.startDate = fromServerDate(options.start_date);
    this.endDate = fromServerDate(options.end_date);

    // DEAL only
    this.dspIDs = options.dsp_ids;
    this.buyerIDs = options.buyer_ids;
    this.dealID = options.deal_id;

    // USED FOR HACKED GROUPINGS FOR DEMOS
    this.logo = options.logo;
    this.from = options.from;
    this.fromLogo = options.fromLogo;

    // HEADER BIDDER SSP only
    this.sspId = options.sspId;
    this.sspLogoUri = options.sspLogoUri;
    this.sspName = options.sspName;

    // BUYER only
    this.advertiserName = options.advertiser;
  }

  get impressionsFormatted() {
    return formatNumber(this.impressions, '0,0');
  }

  get cpmFormatted() {
    return i18n.global.n(this.cpm, 'currency');
  }

  get revenueFormatted() {
    return i18n.global.n(this.revenue, 'currency');
  }

  get impressionsFormattedShort() {
    return formatNumber(this.impressions, '0a').length === 2
      ? formatNumber(this.impressions, '0.0a')
      : formatNumber(this.impressions, '0a');
  }

  get revenueFormattedShort() {
    return formatNumber(this.revenue, '$0a').length === 3
      ? formatNumber(this.revenue, '$0.0a')
      : formatNumber(this.revenue, '$0a');
  }

  get impressionsChange() {
    if (this.previous == null) {
      return null;
    }
    return this.impressions / this.previous.impressions - 1;
  }

  get impressionsChangeFormatted() {
    if (this.previous == null) {
      return '';
    }
    const format: Format = {
      output: 'percent',
      thousandSeparated: true,
      mantissa: this.cpmChange > 1 ? 0 : 1,
    };
    const change = formatNumber(this.impressionsChange, format);
    if (this.impressionsChange === 0 || this.impressionsChange == null) {
      return '';
    }
    return this.impressionsChange > 0 ? `+${change}` : `${change}`;
  }

  get revenueChange() {
    if (this.previous == null) {
      return null;
    }
    return this.revenue / this.previous.revenue - 1;
  }

  get revenueChangeFormatted() {
    if (this.previous == null) {
      return '';
    }
    const format: Format = {
      output: 'percent',
      thousandSeparated: true,
      mantissa: this.revenueChange > 1 ? 0 : 1,
    };
    const change = formatNumber(this.revenueChange, format);
    if (this.revenueChange === 0 || this.revenueChange == null) {
      return '';
    }
    return this.revenueChange > 0 ? `+${change}` : `${change}`;
  }

  get cpmChange() {
    if (this.previous == null) {
      return null;
    }
    return this.cpm / this.previous.cpm - 1;
  }

  get cpmChangeFormatted() {
    if (this.previous == null || this.cpmChange == null || this.cpmChange === 0) {
      return '';
    }
    const format: Format = {
      output: 'percent',
      thousandSeparated: true,
      mantissa: this.cpmChange > 1 ? 0 : 1,
    };
    const change = formatNumber(this.cpmChange.toFixed(5), format);

    return this.cpmChange > 0 ? `+${change}` : `${change}`;
  }
}
